import {
  AnimationMixer,
  Box3,
  BufferAttribute,
  Camera,
  CanvasTexture,
  Group,
  InterleavedBufferAttribute,
  LoopOnce,
  MathUtils,
  Matrix4,
  Mesh,
  Object3D,
  Quaternion,
  Raycaster,
  Sprite,
  SpriteMaterial,
  Vector2,
  Vector3,
  type AnimationClip
} from "three";
import { addBasicObjects } from "./addBasicObjects";
import { createAmbientFishLayer } from "./AmbientFish";
import { SceneProps } from "./SceneProps";
import { animate } from "@/lib/animate";
import { useSceneStore } from "@/lib/sceneStore";
import { getSceneRevealStages } from "./SceneReveal";
import { getPollutionLevelFromCounts } from "./PollutionEffects";
import {
  disposeGarbageObject,
  getGarbageRoot,
  isGarbageHitbox,
  setRandomSafeGarbagePosition,
  showGarbageCollectionFeedback,
  setupGarbageObject,
  updateFloatingGarbageObject
} from "./GarbageObjects";
import { createGarbageHintTracker } from "./GarbageHintTracker";

const AIM_DURATION = 120;
const RETURN_DURATION = 170;
const FALLBACK_CATCH_DURATION = 440;
const NET_ANIMATION_TIME_SCALE = 1.65;
const GARBAGE_FLIGHT_MAX_DURATION = 650;
const GARBAGE_FLIGHT_RATIO = 0.62;
const GARBAGE_LIFT_RATIO = 0.35;
const GARBAGE_LIFT_HEIGHT = 0.36;
const GARBAGE_ARC_BULGE = 0.12;
const SCREEN_CATCH_RADIUS = 0.24;
const SCREEN_CATCH_BOUNDS_PADDING = 0.055;
const SCREEN_CATCH_MIN_HALF_SIZE = 0.075;
const SCORE_FLOAT_DURATION = 1300;
const MAX_AIM_YAW = MathUtils.degToRad(30);
const MAX_AIM_PITCH = MathUtils.degToRad(8);
const NET_BASKET_MESH_NAME = "b_netlow001";
const NET_IDLE_MESH_NAMES = ["b_net.low", "b_netlow001", "b_net.low001"];
const NET_IDLE_DEFORM_MAX_Z = 0.04;
const NET_IDLE_RIM_CLEARANCE = 0.09;
const NET_IDLE_RIM_FADE_HEIGHT = 0.22;
const NET_IDLE_WAVE_AMPLITUDE = 0.026;
const NET_IDLE_SIDE_AMPLITUDE = 0.01;
const NET_FALLBACK_BASKET_MAX_Z = 0;
const NET_CONTENT_FIT_RATIO = 0.78;
const MIN_FIT_SIZE = 0.0001;

type ModelWithAnimations = {
  gltf?: {
    animations?: AnimationClip[];
  } | null;
};
type NetPocket = {
  object: Object3D;
  localPosition: Vector3;
  localFitSize: Vector3;
};
type NetIdlePositionAttribute = BufferAttribute | InterleavedBufferAttribute;
type NetIdleWaveState = {
  mesh: Mesh;
  positionAttribute: NetIdlePositionAttribute;
  originalPositions: Float32Array;
  selectedVertexCount: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
};

const localBoxRootInverse = new Matrix4();
const localBoxMeshMatrix = new Matrix4();
const localBoxCorner = new Vector3();
const geometryVertex = new Vector3();
const screenCatchCorner = new Vector3();
const screenCatchBox = new Box3();

const expandBoxByTransformedCorners = (
  sourceBox: Box3,
  matrix: Matrix4,
  targetBox: Box3
) => {
  for (let ix = 0; ix <= 1; ix += 1) {
    for (let iy = 0; iy <= 1; iy += 1) {
      for (let iz = 0; iz <= 1; iz += 1) {
        localBoxCorner
          .set(
            ix === 0 ? sourceBox.min.x : sourceBox.max.x,
            iy === 0 ? sourceBox.min.y : sourceBox.max.y,
            iz === 0 ? sourceBox.min.z : sourceBox.max.z
          )
          .applyMatrix4(matrix);
        targetBox.expandByPoint(localBoxCorner);
      }
    }
  }
};

const getLocalMeshBox = (
  root: Object3D,
  targetBox: Box3,
  shouldIncludeMesh: (mesh: Mesh) => boolean
) => {
  targetBox.makeEmpty();
  root.updateWorldMatrix(true, true);
  localBoxRootInverse.copy(root.matrixWorld).invert();

  root.traverse((object) => {
    if (
      !(object instanceof Mesh) ||
      !object.visible ||
      !shouldIncludeMesh(object)
    ) {
      return;
    }

    const geometry = object.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }

    if (!geometry.boundingBox) return;

    localBoxMeshMatrix
      .copy(object.matrixWorld)
      .premultiply(localBoxRootInverse);
    expandBoxByTransformedCorners(
      geometry.boundingBox,
      localBoxMeshMatrix,
      targetBox
    );
  });

  return targetBox;
};

const getGeometryVertexBox = (
  mesh: Mesh,
  targetBox: Box3,
  shouldIncludeVertex: (position: Vector3) => boolean
) => {
  const positionAttribute = mesh.geometry.getAttribute("position");
  targetBox.makeEmpty();

  if (!positionAttribute) return targetBox;

  for (let index = 0; index < positionAttribute.count; index += 1) {
    geometryVertex.fromBufferAttribute(positionAttribute, index);
    if (shouldIncludeVertex(geometryVertex)) {
      targetBox.expandByPoint(geometryVertex);
    }
  }

  return targetBox;
};

const getFirstVisibleMesh = (root: Object3D) => {
  let firstMesh: Mesh | null = null;
  root.traverse((object) => {
    if (!firstMesh && object instanceof Mesh && object.visible) {
      firstMesh = object;
    }
  });
  return firstMesh;
};

const getNetIdleMesh = (root: Object3D) => {
  for (const name of NET_IDLE_MESH_NAMES) {
    const object = root.getObjectByName(name);
    if (object instanceof Mesh) return object;
  }

  return getFirstVisibleMesh(root);
};

const createNetIdleWaveState = (stick: Object3D): NetIdleWaveState | null => {
  const mesh = getNetIdleMesh(stick);
  if (!mesh) return null;

  mesh.geometry = mesh.geometry.clone();
  const positionAttribute = mesh.geometry.getAttribute("position");
  if (
    !(positionAttribute instanceof BufferAttribute) &&
    !(positionAttribute instanceof InterleavedBufferAttribute)
  ) {
    return null;
  }

  const originalPositions = new Float32Array(positionAttribute.count * 3);
  let selectedVertexCount = 0;
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < positionAttribute.count; index += 1) {
    const i3 = index * 3;
    const x = positionAttribute.getX(index);
    const y = positionAttribute.getY(index);
    const z = positionAttribute.getZ(index);
    originalPositions[i3] = x;
    originalPositions[i3 + 1] = y;
    originalPositions[i3 + 2] = z;

    if (z > NET_IDLE_DEFORM_MAX_Z) continue;

    selectedVertexCount += 1;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }

  if (selectedVertexCount === 0) return null;

  return {
    mesh,
    positionAttribute,
    originalPositions,
    selectedVertexCount,
    minX,
    maxX,
    minY,
    maxY,
    minZ,
    maxZ,
  };
};

const updateNetIdleWave = (
  state: NetIdleWaveState | null,
  time: number,
  intensity: number
) => {
  if (!state) return;

  const {
    positionAttribute,
    originalPositions,
    minX,
    maxX,
    minY,
    maxY,
    minZ,
    maxZ,
  } = state;
  const width = Math.max(maxX - minX, 0.001);
  const height = Math.max(maxY - minY, 0.001);
  const depth = Math.max(maxZ - minZ, 0.001);
  const clampedIntensity = MathUtils.clamp(intensity, 0, 1);

  for (let index = 0; index < positionAttribute.count; index += 1) {
    const i3 = index * 3;
    const x = originalPositions[i3];
    const y = originalPositions[i3 + 1];
    const z = originalPositions[i3 + 2];

    if (z > NET_IDLE_DEFORM_MAX_Z || clampedIntensity <= 0) {
      positionAttribute.setXYZ(index, x, y, z);
      continue;
    }

    const xNorm = (x - minX) / width - 0.5;
    const yNorm = (y - minY) / height - 0.5;
    const zNorm = (z - minZ) / depth;
    const pocketWeight = 1 - MathUtils.clamp(zNorm, 0, 1);
    const rimDistance = maxY - y;
    const rimMask = MathUtils.clamp(
      (rimDistance - NET_IDLE_RIM_CLEARANCE) / NET_IDLE_RIM_FADE_HEIGHT,
      0,
      1
    );
    const edgeWeight = 0.35 + Math.min(1, Math.abs(xNorm) * 2.2) * 0.65;
    const phase = time * 2.3 + xNorm * 3.8 + yNorm * 2.4 + zNorm * 1.7;
    const wave =
      Math.sin(phase) * 0.74 + Math.sin(phase * 0.57 + 1.1) * 0.26;
    const amount =
      wave * pocketWeight * edgeWeight * rimMask * clampedIntensity;

    positionAttribute.setXYZ(
      index,
      x + amount * NET_IDLE_SIDE_AMPLITUDE * xNorm,
      y +
        Math.sin(phase * 0.82) *
          NET_IDLE_SIDE_AMPLITUDE *
          pocketWeight *
          rimMask *
          clampedIntensity,
      z + amount * NET_IDLE_WAVE_AMPLITUDE
    );
  }

  positionAttribute.needsUpdate = true;
  state.mesh.geometry.computeBoundingSphere();
};

const createNetPocket = (stick: Object3D): NetPocket | null => {
  const namedBasket = stick.getObjectByName(NET_BASKET_MESH_NAME);
  const mesh =
    namedBasket instanceof Mesh ? namedBasket : getFirstVisibleMesh(stick);

  if (!mesh) return null;

  const basketBox = getGeometryVertexBox(
    mesh,
    new Box3(),
    namedBasket instanceof Mesh
      ? () => true
      : (position) => position.z < NET_FALLBACK_BASKET_MAX_Z
  );
  const sourceBox = basketBox.isEmpty()
    ? getGeometryVertexBox(mesh, new Box3(), () => true)
    : basketBox;

  if (sourceBox.isEmpty()) return null;

  return {
    object: mesh,
    localPosition: sourceBox.getCenter(new Vector3()),
    localFitSize: sourceBox.getSize(new Vector3()).multiplyScalar(NET_CONTENT_FIT_RATIO)
  };
};

const getScaleToFitPocket = (
  objectLocalSize: Vector3,
  object: Object3D,
  netPocket: NetPocket
) => {
  const objectWorldScale = object.getWorldScale(new Vector3());
  const objectWorldSize = objectLocalSize.clone().multiply(objectWorldScale);
  const objectMaxSize = Math.max(
    objectWorldSize.x,
    objectWorldSize.y,
    objectWorldSize.z
  );

  const pocketWorldScale = netPocket.object.getWorldScale(new Vector3());
  const pocketWorldSize = netPocket.localFitSize
    .clone()
    .multiply(pocketWorldScale);
  const pocketFitSize = Math.min(
    pocketWorldSize.x,
    pocketWorldSize.y,
    pocketWorldSize.z
  );

  if (objectMaxSize <= MIN_FIT_SIZE || pocketFitSize <= MIN_FIT_SIZE) {
    return 1;
  }

  return Math.min(pocketFitSize / objectMaxSize, 1);
};

const getFirstAnimationClip = (model: unknown) => {
  if (!model || typeof model !== "object" || !("gltf" in model)) {
    return null;
  }

  const gltf = (model as ModelWithAnimations).gltf;
  return gltf?.animations?.[0] ?? null;
};

const prepareGarbageGeometryForCatch = (object: Object3D) => {
  object.updateWorldMatrix(true, true);
  object.traverse((child) => {
    if (!(child instanceof Mesh) || isGarbageHitbox(child)) return;

    child.geometry.computeBoundingBox();
  });
};

const getAimedRotation = (
  screenPoint: Vector2,
  baseRotation: Quaternion,
  target: Quaternion
) => {
  const yaw = -MathUtils.clamp(screenPoint.x, -1, 1) * MAX_AIM_YAW;
  const pitch = MathUtils.clamp(screenPoint.y, -1, 1) * MAX_AIM_PITCH;
  const yawRotation = new Quaternion().setFromAxisAngle(
    new Vector3(0, 1, 0),
    yaw
  );
  const pitchRotation = new Quaternion().setFromAxisAngle(
    new Vector3(1, 0, 0),
    pitch
  );

  return target.copy(baseRotation).multiply(yawRotation).multiply(pitchRotation);
};

const showScoreSprite = (scene: Object3D, position: Vector3, baseMaterial: SpriteMaterial) => {
  const material = baseMaterial.clone();
  material.opacity = 0.5;
  const sprite = new Sprite(material);
  const startPosition = position.clone();
  const endPosition = position.clone();
  endPosition.y += 0.4;
  endPosition.x += Math.random() * 0.5 - 0.25;

  sprite.scale.set(0.1, 0.1, 0.1);
  sprite.position.copy(startPosition);
  scene.add(sprite);

  animate({
    duration: SCORE_FLOAT_DURATION,
    onUpdate: (progress) => {
      const clamped = MathUtils.clamp(progress, 0, 1);
      sprite.position.lerpVectors(
        startPosition,
        endPosition,
        Math.pow(clamped, 0.75)
      );
      sprite.material.opacity = Math.pow(1 - clamped, 2);
    },
    onComplete: () => {
      scene.remove(sprite);
      material.dispose();
    }
  });
};

const getScreenCatchDistance = (
  object: Object3D,
  pointerPos: Vector2,
  camera: Camera
) => {
  const objectLocalBox = getLocalMeshBox(
    object,
    screenCatchBox,
    (mesh) => !isGarbageHitbox(mesh)
  );

  if (objectLocalBox.isEmpty()) return Infinity;

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let projectedCornerCount = 0;

  for (let ix = 0; ix <= 1; ix += 1) {
    for (let iy = 0; iy <= 1; iy += 1) {
      for (let iz = 0; iz <= 1; iz += 1) {
        screenCatchCorner.set(
          ix === 0 ? objectLocalBox.min.x : objectLocalBox.max.x,
          iy === 0 ? objectLocalBox.min.y : objectLocalBox.max.y,
          iz === 0 ? objectLocalBox.min.z : objectLocalBox.max.z
        );
        object.localToWorld(screenCatchCorner);
        screenCatchCorner.project(camera);

        if (screenCatchCorner.z < -1 || screenCatchCorner.z > 1) {
          continue;
        }

        minX = Math.min(minX, screenCatchCorner.x);
        maxX = Math.max(maxX, screenCatchCorner.x);
        minY = Math.min(minY, screenCatchCorner.y);
        maxY = Math.max(maxY, screenCatchCorner.y);
        projectedCornerCount += 1;
      }
    }
  }

  if (projectedCornerCount === 0) return Infinity;

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const halfWidth = Math.max((maxX - minX) / 2, SCREEN_CATCH_MIN_HALF_SIZE);
  const halfHeight = Math.max((maxY - minY) / 2, SCREEN_CATCH_MIN_HALF_SIZE);
  minX = centerX - halfWidth - SCREEN_CATCH_BOUNDS_PADDING;
  maxX = centerX + halfWidth + SCREEN_CATCH_BOUNDS_PADDING;
  minY = centerY - halfHeight - SCREEN_CATCH_BOUNDS_PADDING;
  maxY = centerY + halfHeight + SCREEN_CATCH_BOUNDS_PADDING;

  const dx =
    pointerPos.x < minX ? minX - pointerPos.x : Math.max(0, pointerPos.x - maxX);
  const dy =
    pointerPos.y < minY ? minY - pointerPos.y : Math.max(0, pointerPos.y - maxY);

  return Math.sqrt(dx * dx + dy * dy);
};

export const startStingrayScene = (props: SceneProps) => {
  const { scene, camera, models, onWin, canvas, weaponModel, garbageModels } =
    props;

  const { totalGarbageCount: totalCount } = useSceneStore.getState();

  const roomSize = 5;
  const baseCameraHeight = 2;
  const spawnConfig = props.garbageSpawnConfig;
  const sceneStartedAt = performance.now();
  const getAmbientRevealProgress = () =>
    getSceneRevealStages(props.sceneReveal?.getProgress() ?? 1).decor;
  const getAmbientPollutionLevel = () => {
    const { collectedGarbageCount, totalGarbageCount } =
      useSceneStore.getState();
    return getPollutionLevelFromCounts(collectedGarbageCount, totalGarbageCount);
  };

  const { onExit, onUpdate, hudGroup } = addBasicObjects(props);
  const ambientFish = createAmbientFishLayer(props, {
    roomSize,
    baseCameraHeight,
  });

  const loadedWeaponModel = models[weaponModel.key];
  const stick = loadedWeaponModel?.getModel();
  if (!stick) return { onExit: () => {}, onUpdate: () => {} };

  stick.renderOrder = 1_000_000;
  stick.scale.setScalar(0.75);
  stick.position.set(0, -0.4, -1.25);
  stick.layers.set(1);
  stick.traverse((child) => {
    child.layers.set(1);
  });

  const startRotation = stick.quaternion.clone();
  const startStickPos = stick.position.clone();

  hudGroup.add(stick);

  const weaponAnimationClip = getFirstAnimationClip(loadedWeaponModel);
  const weaponAnimationMixer = weaponAnimationClip
    ? new AnimationMixer(stick)
    : null;
  const weaponAnimationAction =
    weaponAnimationMixer && weaponAnimationClip
      ? weaponAnimationMixer.clipAction(weaponAnimationClip)
      : null;
  const catchDuration = Math.max(
    weaponAnimationClip
      ? (weaponAnimationClip.duration * 1000) / NET_ANIMATION_TIME_SCALE
      : 0,
    FALLBACK_CATCH_DURATION
  );
  const garbageFlightDuration = Math.min(
    catchDuration * GARBAGE_FLIGHT_RATIO,
    GARBAGE_FLIGHT_MAX_DURATION
  );
  const netPocket = createNetPocket(stick);
  const netIdleWave = createNetIdleWaveState(stick);

  if (weaponAnimationAction) {
    weaponAnimationAction.setLoop(LoopOnce, 1);
    weaponAnimationAction.clampWhenFinished = true;
    weaponAnimationAction.timeScale = NET_ANIMATION_TIME_SCALE;
  }

  const playWeaponAnimation = () => {
    if (!weaponAnimationAction) return;

    weaponAnimationAction.reset();
    weaponAnimationAction.setLoop(LoopOnce, 1);
    weaponAnimationAction.clampWhenFinished = true;
    weaponAnimationAction.play();
  };

  const resetWeaponAnimation = () => {
    if (!weaponAnimationAction || !weaponAnimationMixer) return;

    weaponAnimationAction.reset().play();
    weaponAnimationMixer.setTime(0);
    weaponAnimationAction.stop();
  };

  resetWeaponAnimation();

  const objects = new Set<Object3D>();
  let objectsArray: Object3D[] = [];
  const garbageHintTracker = createGarbageHintTracker(objects, camera);
  const occupiedGarbagePositions: Vector3[] = [];
  const goods = garbageModels.map((m) => models[m.key]);
  let isCatching = false;
  let lastAnimationUpdateTime = performance.now() / 1000;

  const plusOneCanvas = document.createElement("canvas");
  plusOneCanvas.width = 100;
  plusOneCanvas.height = 100;
  const plusOneCtx = plusOneCanvas.getContext("2d");
  if (plusOneCtx) {
    plusOneCtx.font = "bold 60px Arial";
    plusOneCtx.fillStyle = "#ffffff";
    plusOneCtx.textAlign = "center";
    plusOneCtx.textBaseline = "middle";
    plusOneCtx.fillText("+1", 50, 50);
  }
  const plusOneTexture = new CanvasTexture(plusOneCanvas);
  const plusOneBaseMaterial = new SpriteMaterial({
    map: plusOneTexture,
    opacity: 0.5,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });

  for (let i = 0; i <= totalCount - 1; i++) {
    const model = goods[i % goods.length];
    const object = model.getModel();
    if (!object) continue;

    setRandomSafeGarbagePosition(object, roomSize, scene, camera, {
      index: i,
      count: totalCount,
      occupiedPositions: occupiedGarbagePositions,
      minRadius: spawnConfig?.minRadius,
      maxRadius: spawnConfig?.maxRadius,
      maxCameraYOffset: spawnConfig?.maxCameraYOffset,
      garbageKey: model.key,
    });
    object.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    setupGarbageObject(object, model.key);
    prepareGarbageGeometryForCatch(object);
    objects.add(object);
    objectsArray.push(object);
    occupiedGarbagePositions.push(object.position.clone());
    scene.add(object);
  }

  const handleClick = (event: PointerEvent) => {
    // Во время образовательного факта игру не останавливаем целиком:
    // Рендер продолжает работать, но тапы по мусору не засчитываются.
    if (useSceneStore.getState().isGamePaused || isCatching) return;

    const { clientX, clientY } = event;

    const { width, height } = canvas.getBoundingClientRect();
    const x = (clientX / width) * 2 - 1;
    const y = -(clientY / height) * 2 + 1;

    const raycaster = new Raycaster();
    raycaster.setFromCamera(new Vector2(x, y), camera);

    const pointerPos = new Vector2(x, y);
    const objectWorldPosition = new Vector3();
    const projectedPosition = new Vector3();
    let selectedObject: Group | null = null;

    const intersects = raycaster.intersectObjects(objectsArray, true);
    for (const intersect of intersects) {
      const parentObj = getGarbageRoot(intersect.object) as Group | null;
      if (parentObj && objects.has(parentObj) && !parentObj.userData.hasHit) {
        selectedObject = parentObj;
        break;
      }
    }

    if (!selectedObject) {
      let closestDistance = Infinity;

      objects.forEach((object) => {
        if (object.userData.hasHit) return;

        object.getWorldPosition(objectWorldPosition);
        projectedPosition.copy(objectWorldPosition).project(camera);

        if (projectedPosition.z < -1 || projectedPosition.z > 1) return;

        const boundsDistance = getScreenCatchDistance(
          object,
          pointerPos,
          camera
        );
        const centerDistance = pointerPos.distanceTo(projectedPosition);
        const distance = Math.min(boundsDistance, centerDistance);
        if (distance < closestDistance && distance < SCREEN_CATCH_RADIUS) {
          closestDistance = distance;
          selectedObject = object as Group;
        }
      });
    }

    if (!selectedObject) return;

    isCatching = true;
    updateNetIdleWave(netIdleWave, performance.now() / 1000, 0);
    playWeaponAnimation();
    const parentObj = selectedObject;
    parentObj.userData.hasHit = true;
    showGarbageCollectionFeedback(scene, parentObj);
    objects.delete(parentObj);
    objectsArray.splice(objectsArray.indexOf(parentObj), 1);
    const objectParent = parentObj.parent;

    const objectLocalBox = getLocalMeshBox(
      parentObj,
      new Box3(),
      (mesh) => !isGarbageHitbox(mesh)
    );
    const objectLocalCenter = objectLocalBox.isEmpty()
      ? new Vector3()
      : objectLocalBox.getCenter(new Vector3());
    const objectLocalSize = objectLocalBox.isEmpty()
      ? new Vector3()
      : objectLocalBox.getSize(new Vector3());
    const startScale = parentObj.scale.clone();
    const caughtScale = startScale.clone();

    if (netPocket) {
      caughtScale.multiplyScalar(
        getScaleToFitPocket(objectLocalSize, parentObj, netPocket)
      );
    }

    parentObj.getWorldPosition(objectWorldPosition);
    projectedPosition.copy(objectWorldPosition).project(camera);
    const aimPoint =
      projectedPosition.z >= -1 && projectedPosition.z <= 1
        ? new Vector2(projectedPosition.x, projectedPosition.y)
        : pointerPos;
    const targetRotation = getAimedRotation(
      aimPoint,
      startRotation,
      new Quaternion()
    );
    const qaStick = new Quaternion();
    const returnStartRotation = new Quaternion();
    const scoreStartPosition = new Vector3();
    const centerOffsetLocal = new Vector3();
    const startCenterLocal = new Vector3();
    const liftCenterLocal = new Vector3();
    const pocketCenterLocal = new Vector3();
    const desiredCenterLocal = new Vector3();

    const getObjectCenterInParentLocal = (scale: Vector3, target: Vector3) => {
      centerOffsetLocal
        .copy(objectLocalCenter)
        .multiply(scale)
        .applyQuaternion(parentObj.quaternion);
      return target.copy(parentObj.position).add(centerOffsetLocal);
    };
    const setObjectCenterInParentLocal = (center: Vector3) => {
      centerOffsetLocal
        .copy(objectLocalCenter)
        .multiply(parentObj.scale)
        .applyQuaternion(parentObj.quaternion);
      parentObj.position.copy(center).sub(centerOffsetLocal);
    };
    const getPocketCenterInParentLocal = (target: Vector3) => {
      if (!netPocket || !objectParent) return target.copy(startCenterLocal);

      netPocket.object.updateWorldMatrix(true, false);
      objectParent.updateWorldMatrix(true, false);
      target.copy(netPocket.localPosition);
      netPocket.object.localToWorld(target);
      objectParent.worldToLocal(target);
      return target;
    };

    getObjectCenterInParentLocal(startScale, startCenterLocal);
    liftCenterLocal.copy(startCenterLocal);
    liftCenterLocal.y += GARBAGE_LIFT_HEIGHT;

    animate({
      duration: AIM_DURATION,
      onUpdate: (progress) => {
        const clamped = MathUtils.clamp(progress, 0, 1);
        const eased = clamped * clamped * (3 - 2 * clamped);
        qaStick.copy(startRotation).slerp(targetRotation, eased);
        stick.quaternion.copy(qaStick);
      },
      onComplete: () => {
        stick.quaternion.copy(targetRotation);
      }
    });

    if (netPocket && objectParent) {
      animate({
        duration: garbageFlightDuration,
        onUpdate: (progress) => {
          const clamped = MathUtils.clamp(progress, 0, 1);
          const easedScale = clamped * clamped * (3 - 2 * clamped);
          parentObj.scale.lerpVectors(startScale, caughtScale, easedScale);

          if (clamped < GARBAGE_LIFT_RATIO) {
            const liftProgress = clamped / GARBAGE_LIFT_RATIO;
            const easedLift = 1 - Math.pow(1 - liftProgress, 3);
            desiredCenterLocal.lerpVectors(
              startCenterLocal,
              liftCenterLocal,
              easedLift
            );
          } else {
            const flightProgress =
              (clamped - GARBAGE_LIFT_RATIO) / (1 - GARBAGE_LIFT_RATIO);
            const easedFlight =
              flightProgress * flightProgress * (3 - 2 * flightProgress);
            getPocketCenterInParentLocal(pocketCenterLocal);
            desiredCenterLocal.lerpVectors(
              liftCenterLocal,
              pocketCenterLocal,
              easedFlight
            );
            desiredCenterLocal.y +=
              Math.sin(flightProgress * Math.PI) * GARBAGE_ARC_BULGE;
          }

          setObjectCenterInParentLocal(desiredCenterLocal);
        },
        onComplete: () => {
          parentObj.scale.copy(caughtScale);
          getPocketCenterInParentLocal(pocketCenterLocal);
          setObjectCenterInParentLocal(pocketCenterLocal);
          netPocket.object.attach(parentObj);
        }
      });
    }

    animate({
      duration: catchDuration,
      onUpdate: () => {},
      onComplete: () => {
        parentObj.getWorldPosition(scoreStartPosition);
        scene.worldToLocal(scoreStartPosition);
        parentObj.parent?.remove(parentObj);
        disposeGarbageObject(parentObj);

        const {
          setCollectedGarbageCount,
          collectedGarbageCount,
          totalGarbageCount
        } = useSceneStore.getState();

        setCollectedGarbageCount(collectedGarbageCount + 1);
        showScoreSprite(scene, scoreStartPosition, plusOneBaseMaterial);

        if (collectedGarbageCount + 1 >= totalGarbageCount) {
          onWin();
        }

        resetWeaponAnimation();
        returnStartRotation.copy(stick.quaternion);
        animate({
          duration: RETURN_DURATION,
          onUpdate: (progress) => {
            const clamped = MathUtils.clamp(progress, 0, 1);
            const eased = clamped * clamped * (3 - 2 * clamped);
            qaStick.copy(returnStartRotation).slerp(startRotation, eased);
            stick.quaternion.copy(qaStick);
          },
          onComplete: () => {
            stick.position.copy(startStickPos);
            stick.quaternion.copy(startRotation);
            isCatching = false;
          }
        });
      }
    });
  };

  canvas.addEventListener("pointerdown", handleClick);

  return {
    onExit: () => {
      ambientFish.dispose();
      objects.forEach(disposeGarbageObject);
      objects.clear();
      objectsArray = [];
      weaponAnimationMixer?.stopAllAction();
      garbageHintTracker.dispose();
      plusOneTexture.dispose();
      plusOneBaseMaterial.dispose();
      onExit();
      canvas.removeEventListener("pointerdown", handleClick);
    },
    onUpdate: () => {
      const animationUpdateTime = performance.now() / 1000;
      const animationDeltaSeconds = Math.min(
        animationUpdateTime - lastAnimationUpdateTime,
        0.05
      );
      weaponAnimationMixer?.update(animationDeltaSeconds);
      lastAnimationUpdateTime = animationUpdateTime;
      updateNetIdleWave(
        netIdleWave,
        animationUpdateTime,
        useSceneStore.getState().isGamePaused || isCatching ? 0 : 1
      );
      onUpdate();
      ambientFish.update(
        (performance.now() - sceneStartedAt) / 1000,
        getAmbientRevealProgress(),
        getAmbientPollutionLevel()
      );

      objects.forEach((object) => {
        updateFloatingGarbageObject(
          object,
          roomSize,
          baseCameraHeight,
          scene,
          camera,
          {
            clampMaxYToCamera: true,
            maxCameraYOffset: spawnConfig?.maxCameraYOffset,
            deltaSeconds: animationDeltaSeconds,
          }
        );
      });
      garbageHintTracker.update(animationUpdateTime);
    }
  };
};
